import { gql, useQuery } from '@apollo/client';
import { FC } from 'react';
import { useParams } from 'react-router-dom';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';

import LoadingIndicator from 'primitives/LoadingIndicator';

const CLOSINGS_QUERY = gql(`
    query fetchClosingsByDeal($dealId: ID!, $cursor: ID, $limit: Int, $filters: ClosingsFilterType) {
      deal(id: $dealId) {
        id
        syndicate {
          type
        }
        closings(cursor: $cursor, limit: $limit, filters: $filters) {
          nodes {
            id
            amount
            status

            fundDealClosingParticipants {
              id
              status
              amountInvested
              ownershipPercentage
              optedInAt
              optedOutAt
              investmentEntity {
                id
                name
                user {
                  id
                  name
                }
              }
            }
            fundDealClosingRebalanceParticipants {
              id
              status
              additionalAmount
              newOwnershipPercentage
              consentRequestedAt
              consentRespondedAt
              investmentEntity {
                id
                name
                user {
                  id
                  name
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            cursor
          }
        }
      }
    }
  `);

const FundDealCommitments: FC = () => {
  const { dealId } = useParams<{ dealId: string }>() as { dealId: string };

  const { data, loading, error } = useQuery(CLOSINGS_QUERY, {
    variables: {
      dealId: dealId,
    },
  });

  if (loading) return <LoadingIndicator />;
  if (error) return <ErrorMessage error={error} />;

  const decorateFundClosingParticipants = fundClosingParticipants => {
    if (fundClosingParticipants.length === 0) return [];

    return fundClosingParticipants.map(participant => {
      return {
        ...participant,
        status: participant.optedInAt
          ? 'OPTED_IN'
          : participant.optedOutAt
          ? 'OPTED_OUT'
          : 'PENDING', // FOR UI PURPOSES ONLY
      };
    });
  };

  const closings = data.deal.closings.nodes;

  return (
    <div className="mt-4">
      <h1 className="text-lg font-bold mb-4">Commitments</h1>

      {closings.map(closing => (
        <div key={closing.id}>
          <DataTable
            data={decorateFundClosingParticipants(closing.fundDealClosingParticipants)}
            columns={[
              {
                label: 'Investment Entity',
                fieldName: 'investmentEntity.name',
              },
              {
                label: 'Investor',
                fieldName: 'investmentEntity.user.name',
              },
              {
                label: 'Amount Invested',
                fieldName: 'amountInvested',
                type: 'CURRENCY',
              },
              {
                label: 'Ownership Percentage',
                fieldName: 'ownershipPercentage',
                type: 'PERCENTAGE_WITH_DECIMAL',
              },
              {
                label: 'Opted In At',
                fieldName: 'optedInAt',
                type: 'DATE',
              },
              {
                label: 'Opted Out At',
                fieldName: 'optedOutAt',
                type: 'DATE',
              },
              {
                label: 'Status',
                fieldName: 'status',
                type: 'STATUS',
              },
            ]}
          />
          <p className="text-sm text-gray-500">
            * If an investor opts out, then you will need to rebalance the commitment(s) for this
            deal.
          </p>
          <h1 className="text-lg font-bold mb-4 mt-8">Rebalance Participants</h1>
          <DataTable
            data={decorateFundClosingParticipants(closing.fundDealClosingRebalanceParticipants)}
            columns={[
              {
                label: 'Investment Entity',
                fieldName: 'investmentEntity.name',
              },
              {
                label: 'Investor',
                fieldName: 'investmentEntity.user.name',
              },

              {
                label: 'Additional Amount',
                fieldName: 'additionalAmount',
                type: 'CURRENCY',
              },

              {
                label: 'New Ownership(%)',
                fieldName: 'newOwnershipPercentage',
                type: 'PERCENTAGE_WITH_DECIMAL',
              },

              {
                label: 'Requested At',
                fieldName: 'consentRequestedAt',
                type: 'DATE',
              },

              {
                label: 'Responded At',
                fieldName: 'consentRespondedAt',
                type: 'DATE',
              },

              {
                label: 'Status',
                fieldName: 'status',
                type: 'STATUS',
              },
            ]}
          />
        </div>
      ))}
    </div>
  );
};

export default FundDealCommitments;
